// The one label facade every template (and classic, as it is extracted)
// reads fan wording from. Shared and keyed by sport, never per template.
// See docs/site-templates/LABELS.md.

import { useMemo } from 'react'
import { useOptionalSportContext, type SportPack } from '@/contexts/SportContext'
import { sportWords } from '@/contexts/sportPackI18n'
import { glossary, type GlossaryKey } from './glossary'
import {
  FINAL_TERM,
  FORMAT_KEY,
  STATS_VIEW_KEY,
  competitionPhase,
  gameStatusKind,
  labelWords,
  roundName,
  type BracketTeamStatus,
  type CompetitionFormat,
} from './enums'
import { defaultVenueLabel, resolveTerms } from './terms'
import { buildUi } from './ui'
import { messages, useLocale } from '@/i18n'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'
import type { Labels, TermCase, TermFn, TermKey } from './types'

/** Singular → plural, for term(key, { count }). */
const PLURALS: Partial<Record<TermKey, TermKey>> = {
  game: 'games',
  venue: 'venues',
  season: 'seasons',
  competition: 'competitions',
  liveEvent: 'liveEvents',
  division: 'divisions',
}

export function applyCase(value: string, c: TermCase | undefined): string {
  switch (c) {
    case 'lower':
      return value.toLowerCase()
    case 'sentence':
      return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
    case 'title':
      return value.replace(/(^|[\s-])(\p{L})/gu, (_m, sep: string, ch: string) => sep + ch.toUpperCase())
    default:
      return value
  }
}

/** The parts of a sport pack the labels read. */
export type PackLike = Pick<SportPack, 'id'> & Partial<Pick<SportPack, 'terms' | 'venueLabel'>>

/**
 * Build the labels for one sport pack, in the active language (#1118). Pure:
 * controllers, fixtures and tests can call it without React. `pack` may be
 * null (no pack yet): basketball's defaults apply.
 */
export function createLabels(pack: PackLike | null | undefined): Labels {
  const sportId = pack?.id ?? 'basketball'
  const terms = resolveTerms(pack)
  const venue = sportWords(sportId)?.venue ?? pack?.venueLabel ?? defaultVenueLabel(pack?.id)
  const words = labelWords()
  const nouns = glossary()

  const raw = (key: TermKey): string => {
    if (key === 'venue') return venue.singular
    if (key === 'venues') return venue.plural
    if (key in terms) return terms[key as keyof typeof terms]
    return nouns[key as GlossaryKey]
  }

  const term: TermFn = (key, o) => {
    const plural = o?.count !== undefined && o.count !== 1 ? PLURALS[key] : undefined
    return applyCase(raw(plural ?? key), o?.case)
  }

  return {
    sportId,
    terms,
    term,
    status(game, o) {
      const timing =
        game.startsAt !== undefined ? { startsAt: game.startsAt, hasScore: game.hasScore, now: game.now } : undefined
      const kind = gameStatusKind(game.status, game.liveUpdates ?? true, timing)
      const variant = o?.variant ?? 'badge'
      // The variants share one wording today; `final` is the pack's term.
      const text = kind === 'final' ? terms[FINAL_TERM[variant]] : words.gameStatus[kind]
      return { kind, text }
    },
    competitionProgress(c) {
      const phase = competitionPhase(c)
      if (phase === 'completed') return words.progress.completed
      if (phase === 'in_progress') return words.progress.inProgress
      return c.startsAt ? words.progress.startsOn(c.startsAt) : words.progress.upcoming
    },
    format(type) {
      // Anything that is not round robin reads as the bracket format, like
      // today's ternary (Schedule.tsx:279).
      return words.format[FORMAT_KEY[type as CompetitionFormat] ?? FORMAT_KEY.single_elimination]
    },
    bracketStatus(s) {
      if (!s) return '—'
      return words.bracketStatus[s as BracketTeamStatus] ?? s
    },
    roundName,
    statsView(view, subject) {
      return words.statsView[subject][STATS_VIEW_KEY[view]]
    },
    ui: buildUi(term, sportId, messages(siteCoreMessages)),
  }
}

/**
 * Labels for the active sport (or `sportId` when given, e.g. a page scoped
 * to an entity's sport). Works outside a SportProvider too (kit components
 * rendered in isolation): basketball's defaults apply.
 */
export function useLabels(sportId?: string): Labels {
  const ctx = useOptionalSportContext()
  const id = sportId || ctx?.activeSportId || 'basketball'
  const pack = ctx?.packs[id] ?? null
  // Every word comes from the active language's catalogues (#1063, #1118).
  const locale = useLocale()
  return useMemo(
    () => createLabels(pack ?? { id }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- locale is read inside createLabels
    [pack, id, locale]
  )
}
